// src/components/forms/components/CategorySelector.tsx
import React, { useState } from 'react';
import { Control } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface CategorySelectorProps {
  control: Control<any>;
  categories: string[];
  onAddCategory: (category: string) => Promise<void>;
}

const defaultCategories = [
  'personal', 'family', 'health', 'work', 'spiritual', 'gratitude',
  'guidance', 'protection', 'healing', 'relationships', 'finances', 'other'
];

export default function CategorySelector({ control, categories, onAddCategory }: CategorySelectorProps) {
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const allCategories = [...new Set([...defaultCategories, ...categories])];

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    setIsAddingCategory(true);
    try {
      await onAddCategory(newCategory.trim());
      setNewCategory('');
      setIsDialogOpen(false);
    } finally {
      setIsAddingCategory(false);
    }
  };

  return (
    <FormField control={control} name="category" render={({ field }) => (
      <FormItem>
        <FormLabel className="text-sm font-medium text-foreground">Category</FormLabel>
        <div className="flex gap-2">
          <FormControl className="flex-1">
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <SelectTrigger className="bg-background border-border text-foreground">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              {/* ✅ Mobile-safe dropdown */}
              <SelectContent className="bg-background border border-border z-[200] max-h-[40dvh] overflow-y-auto">
                {allCategories.map((category) => (
                  <SelectItem
                    key={category}
                    value={category}
                    className="hover:bg-accent capitalize"
                  >
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="icon" className="flex-shrink-0">
                <Plus size={16} />
              </Button>
            </DialogTrigger>
            <DialogContent className="z-[250]">
              <DialogHeader>
                <DialogTitle>Add Custom Category</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter category name"
                  maxLength={30}
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleAddCategory}
                    disabled={isAddingCategory || !newCategory.trim()}
                  >
                    {isAddingCategory ? 'Adding...' : 'Add Category'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <FormMessage />
      </FormItem>
    )}/>
  );
}
